import { useRef, useState } from 'react';
import { useLatestUnseenAnnouncement, useMarkAnnouncementsSeen } from './useAnnouncements';
import { AnnouncementModal } from './AnnouncementModal';
import { usePendingInvitations } from '@/hooks/usePendingInvitations';
import { useNewCabinets } from '@/hooks/useNewCabinets';
import { useManualPaywall } from '@/hooks/useManualPaywall';

/**
 * Porte d'affichage des annonces produit, montée dans DashboardLayout.
 *
 * `blocked` : prédicat fourni par DashboardLayout, vrai si une dialog globale
 * pilotée par un state local du layout est active (no-sub / trial / addin), ou
 * si la fenêtre d'accueil pilotée par l'URL est ouverte (upgrade_success /
 * trial_started).
 *
 * Auto-déférence (Codex P1-5) : trois overlays autonomes (PendingInvitations,
 * NewCabinets, ManualPaywall) n'exposent aucun booléen à DashboardLayout — ils
 * lisent leur propre hook. On consulte ICI exactement les MÊMES hooks pour ne
 * jamais empiler l'annonce par-dessus l'un d'eux (réutilisation, pas de logique
 * réinventée). On défère aussi pendant leur phase de chargement : sinon
 * l'annonce flash une fraction de seconde au premier login avant que la query
 * invitations/paywall ne résolve, puis disparaît sous l'overlay (anti-flicker).
 */
export function AnnouncementGate({ blocked }: { blocked: boolean }) {
  const latest = useLatestUnseenAnnouncement();
  const markSeen = useMarkAnnouncementsSeen();
  const [dismissed, setDismissed] = useState(false);
  const firedRef = useRef(false); // P1-6 : anti double-close

  // Mêmes signaux que les composants overlay eux-mêmes (réutilisation stricte).
  const { data: invitations, isLoading: invitationsLoading } = usePendingInvitations();
  const { data: newCabinets, isLoading: newCabinetsLoading } = useNewCabinets();
  const { isPaywallActive, isLoading: paywallLoading } = useManualPaywall();

  // GA depuis le 2026-05-16 : plus de gating par feature flag. La modale
  // s'affiche à tout membre dont la dernière annonce non vue est ≥ son
  // `joined_at` (filtre porté par useLatestUnseenAnnouncement). L'ancien
  // flag `beta_announcements` a été retiré du catalogue (déprécié) — ne pas
  // réintroduire de garde flag ici sans décision produit explicite.
  const hasPendingInvitations = (invitations?.length ?? 0) > 0;
  const hasNewCabinets = (newCabinets?.length ?? 0) > 0;

  if (
    blocked ||
    !latest ||
    dismissed ||
    invitationsLoading ||
    // Asymétrie volontaire : NewCabinetsDialog ne gate PAS sur son isLoading
    // (seulement sur hasNewCabinets). Ici on défère AUSSI pendant le chargement
    // — plus conservateur que la dialog — pour éviter un flash de l'annonce au
    // premier login avant que la query newCabinets ne résolve. Ne pas "simplifier"
    // en alignant 1:1 sur la dialog.
    newCabinetsLoading ||
    paywallLoading ||
    hasPendingInvitations ||
    hasNewCabinets ||
    isPaywallActive
  ) {
    return null;
  }

  const handleClose = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    setDismissed(true);            // ferme tout de suite (optimiste UI)
    // WHY (révisé 2026-06 — centre de notifications) : on ne marque vu QUE
    // l'annonce affichée. Les autres non-lues restent rattrapables via le
    // centre (menu profil → Notifications) au lieu d'être englouties. Au
    // prochain login, la modale poussera la non-lue suivante (selectLatestUnseen),
    // une par session.
    markSeen.mutate([latest.id]);
  };

  return <AnnouncementModal announcement={latest} open onClose={handleClose} />;
}
